import PropTypes from 'prop-types';
import React, { useState } from 'react';

import useIntersect from '../../../hooks/useIntersect';
import t from '../../../labels/labelling-utils';
import { IReportsDetailsMatch, MatchReportStatusList } from '../../../typings/match-reports';
import { isPreMatch } from '../../../utils/match-status';
import { slugify } from '../../../utils/slugify';
import Cta from '../../atoms/cta/cta';
import ErrorComponent from '../../atoms/error/error';
import Spacer from '../../atoms/spacer/spacer';
import Team, { ITeam } from '../../molecules/team/team';
import ReportTabs from '../../organisms/report-tabs/report-tabs';
import ErrorBoundary from '../error-boundary/error-boundary';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { matchPhaseArray, MatchStatusList, nonLinearCompetitionPhasesArray } from '../../../typings/match';
import { componentSizeList } from '../../../typings/design-tokens';
import { MatchRowSources } from '../../../typings/match-row';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { IIcon } from '../../atoms/icons/icon';
import IconArrowLeft from '../../atoms/icons/icon-arrow-left';
import IconArrowRight from '../../atoms/icons/icon-arrow-right';
import { GliderWrapper, IMenuGliderItems, MenuGliderItems } from '../../organisms';
import NoSSR from '../../../context/ssr/no-ssr';

export interface IMatchReportsDetail {
  matchDayList: Array<IReportsDetailsMatch>;
  reportsHomepageUrl?: string;
}

interface ICreateIds {
  home: string;
  away: string;
  full: string;
}

const isValidMatchList = (matchDayList: IReportsDetailsMatch[]) => Boolean(matchDayList && matchDayList.length);

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

/** TODO: WORKAROUND TO REMOVE according with BE implementations */
const remapGoalsOnPrematchStatus = (match: IReportsDetailsMatch) => {
  if (isPreMatch(match.status)) {
    match.home.goal = '-';
    match.away.goal = '-';
  }
};

const buildGliderLabel = (homeShortname: string | undefined, awayShortname: string | undefined): React.ReactNode => (
  <React.Fragment>
    {homeShortname?.toUpperCase() || ''}
    <br />
    {awayShortname?.toUpperCase() || ''}
  </React.Fragment>
);

const createIds = (home: ITeam, away: ITeam, fallbackKey: number): ICreateIds => {
  const idHome = slugify(/* istanbul ignore next: optional chaining */ home.seoName?.toLowerCase() || `${fallbackKey}`);
  const idAway = slugify(/* istanbul ignore next: optional chaining */ away.seoName?.toLowerCase() || `${fallbackKey}`);
  return {
    home: `${idHome}-home`,
    away: `${idAway}-away`,
    full: `${idHome}-${idAway}`,
  };
};

const getGliderItems = (matchDayList: IReportsDetailsMatch[]) =>
  matchDayList.map((match: IReportsDetailsMatch, key: number) => ({
    href: `#${createIds(match.home, match.away, key).full}`,
    label: buildGliderLabel(
      /* istanbul ignore next: optional chaining */ match?.home?.shortname,
      /* istanbul ignore next: optional chaining */ match?.away?.shortname
    ),
  }));

const classNames = (): IClassNames =>
  makeClassNames({
    matchReportsDetail: 'match-reports-detail',
    gliderWrapper: 'match-reports-detail--wrapper',
  });

const iconLeft = (): React.ReactElement<IIcon> => <IconArrowLeft size="xxxsmall" color="blue" />;
const iconRight = (): React.ReactElement<IIcon> => <IconArrowRight size="xxxsmall" color="blue" />;

const ssrPlaceholder = (props: IMenuGliderItems, cn: IClassNames) => {
  return (
    <div className={cn.gliderWrapper}>
      <MenuGliderItems {...props} isSSR />
    </div>
  );
};

const MatchReportsDetail: React.FC<IMatchReportsDetail> = ({ matchDayList, reportsHomepageUrl }) => {
  const cn = classNames();
  const gliderItems = React.useMemo(() => getGliderItems(matchDayList), [matchDayList]);
  const [activeIndex, setActiveIndex] = useState(0);

  const onIntersect = (item: HTMLElement) => {
    setActiveIndex(Number(item.dataset.index));
  };

  const onSelectedGliderItem = (gliderIndex: number) => {
    if (gliderIndex !== activeIndex) {
      setActiveIndex(gliderIndex);
    }
  };

  useIntersect('[data-intersect]', onIntersect);

  const gliderComponentProps: IMenuGliderItems = {
    items: gliderItems,
    activeSlideIndex: activeIndex,
    variant: 'mgv',
    onItemSelectedCallback: onSelectedGliderItem,
  };

  return (
    <div className={cn.matchReportsDetail}>
      <ErrorBoundary hasError={!isValidMatchList(matchDayList)} errorComponent={getErrorComponent()}>
        {reportsHomepageUrl && (
          <React.Fragment>
            <Cta href={reportsHomepageUrl} color="blue" iconPosition="left">
              {t('match.reports.detail.back.cta')}
            </Cta>
            <Spacer size={20} />
          </React.Fragment>
        )}
        <NoSSR placeholder={ssrPlaceholder(gliderComponentProps, cn)}>
          <GliderWrapper
            propsComponent={gliderComponentProps}
            Component={MenuGliderItems}
            itemWidth={66}
            duration={2}
            bordered="bottom"
            iconLeft={iconLeft()}
            iconRight={iconRight()}
            hasArrows
            scrollLock
            exactWidth
            topOffset
          />
        </NoSSR>
        <Spacer size={30} />
        {matchDayList.map((match: IReportsDetailsMatch, key: number) => {
          const ids = createIds(match.home, match.away, key);
          remapGoalsOnPrematchStatus(match); // TODO: workaround to remove!
          return (
            <div id={ids.full} key={ids.full} data-intersect>
              <ReportTabs {...match} idHome={ids.home} idAway={ids.away} data-intersect />
            </div>
          );
        })}
      </ErrorBoundary>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchReportsDetail.propTypes = {
    matchDayList: PropTypes.arrayOf(
      PropTypes.shape({
        away: PropTypes.exact(Team.propTypes!).isRequired,
        competitionPhase: PropTypes.oneOf(nonLinearCompetitionPhasesArray),
        competitionPhaseRound: PropTypes.oneOf(matchPhaseArray),
        competitionPhaseWinnerTeamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        cta: isPropTypeNullable(PropTypes.string),
        customCompetitionName: PropTypes.string,
        date: isPropTypeNullable(PropTypes.string),
        externalInterval: PropTypes.number,
        footer: isPropTypeNullable(PropTypes.node),
        header: isPropTypeNullable(PropTypes.node),
        home: PropTypes.exact(Team.propTypes!).isRequired,
        id: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        label: isPropTypeNullable(PropTypes.string),
        link: isPropTypeNullable(PropTypes.string),
        matchCurrentDateTime: isPropTypeNullable(PropTypes.string),
        referees: PropTypes.arrayOf(
          PropTypes.shape({
            fullname: PropTypes.string,
            id: PropTypes.number.isRequired,
            name: PropTypes.string,
            surname: PropTypes.string,
          }).isRequired
        ).isRequired,
        reportStatus: isPropTypeNullable(PropTypes.oneOf(MatchReportStatusList).isRequired),
        reportUrl: isPropTypeNullable(PropTypes.string),
        round: isPropTypeNullable(PropTypes.number),
        roundGroup: isPropTypeNullable(PropTypes.string),
        roundName: isPropTypeNullable(PropTypes.string),
        showLiveResult: isPropTypeNullable(PropTypes.bool),
        size: PropTypes.oneOf(componentSizeList),
        status: PropTypes.oneOf(MatchStatusList).isRequired,
        statusStartDateTime: isPropTypeNullable(PropTypes.string),
        timerInterval: isPropTypeNullable(PropTypes.number),
        trackingData: isPropTypeNullable(PropTypes.object),
        type: isPropTypeNullable(PropTypes.oneOf(MatchRowSources)),
        venue: isPropTypeNullable(PropTypes.string),
      }).isRequired
    ).isRequired,
    reportsHomepageUrl: PropTypes.string,
  };
}

export default MatchReportsDetail;
